import React, { useState } from "react";
import {
    validateSiteUrl,
    validateCompanyId,
    validatePublicKey,
    validatePrivateKey,
    canTest,
} from "../../Helpers/BillingSyncValidation";

/**
 * Task 37, B1 — the reseller's ConnectWise connection details.
 *
 * Every rule here comes from Helpers/BillingSyncValidation.js; this component renders fields and
 * reports changes back. It performs no validation of its own, so what the form permits and what the
 * server accepts cannot disagree.
 *
 * THE PRIVATE KEY IS WRITE-ONLY. It is never returned by the API, so the field is always blank on
 * load. When a key is already stored, leaving it blank keeps it — which is what lets somebody correct
 * a company id without going to find the key again.
 */
export default function ConnectionForm({
    form,
    onChange,
    hasStoredKey,
    testResult,
    testing,
    saving,
    onTest,
    onSave,
}) {
    const set = (patch) => onChange({ ...form, ...patch });

    // The public key is half of a credential pair and was rendered in clear on every load, where
    // anyone beside the screen could read it off. It is not a secret on its own — the private key
    // is what protects the account, and that is never returned at all — but a stored credential
    // should not be on display by default. Revealable, because a reseller checking which key is
    // configured is the ordinary reason to come to this screen.
    const [showPublicKey, setShowPublicKey] = useState(false);

    const siteCheck = validateSiteUrl(form.siteUrl);
    const companyCheck = validateCompanyId(form.companyId);
    const publicCheck = validatePublicKey(form.publicKey);
    const privateCheck = validatePrivateKey(form.privateKey, hasStoredKey);

    const testable = canTest(form, hasStoredKey).valid;
    const busy = testing || saving;

    // Saving requires a test that passed for the details CURRENTLY in the form. The page clears
    // testResult on every edit, so this can never approve a save against values nobody tested.
    const saveable = testResult != null && testResult.success === true;

    return (
        <div style={{ maxWidth: 680 }}>
            <p className="text-muted">
                Enter the details of your own ConnectWise environment. Nothing is saved until a
                connection test passes.
            </p>

            <div className="mb-3">
                <label className="form-label fw-semibold" htmlFor="cw-site-url">
                    ConnectWise address
                </label>
                <input
                    id="cw-site-url"
                    type="text"
                    className="form-control"
                    value={form.siteUrl || ""}
                    disabled={busy}
                    placeholder="https://api-eu.myconnectwise.net"
                    onChange={(e) => set({ siteUrl: e.target.value })}
                />
                {/* N7 — the version path is ours to supply, so a reseller cannot get it wrong and
                    a ConnectWise version change does not mean every reseller editing their own. */}
                <div className="text-muted" style={{ fontSize: 13 }}>
                    Your ConnectWise address only. The API version path is added for you.
                </div>
                {form.siteUrl && !siteCheck.valid && (
                    <div className="text-danger" style={{ fontSize: 13 }}>{siteCheck.message}</div>
                )}
            </div>

            <div className="mb-3">
                <label className="form-label fw-semibold" htmlFor="cw-company-id">
                    Company ID
                </label>
                <input
                    id="cw-company-id"
                    type="text"
                    className="form-control"
                    value={form.companyId || ""}
                    disabled={busy}
                    onChange={(e) => set({ companyId: e.target.value })}
                />
                {form.companyId !== undefined && form.companyId !== "" && !companyCheck.valid && (
                    <div className="text-danger" style={{ fontSize: 13 }}>{companyCheck.message}</div>
                )}
            </div>

            <div className="mb-3">
                <label className="form-label fw-semibold" htmlFor="cw-public-key">
                    Public key
                </label>
                <input
                    id="cw-public-key"
                    type={showPublicKey ? "text" : "password"}
                    className="form-control"
                    value={form.publicKey || ""}
                    disabled={busy}
                    autoComplete="off"
                    onChange={(e) => set({ publicKey: e.target.value })}
                />
                <button
                    type="button"
                    className="btn btn-sm btn-link p-0"
                    style={{ fontSize: 13 }}
                    onClick={() => setShowPublicKey((v) => !v)}
                >
                    {showPublicKey ? "Hide" : "Show"}
                </button>
                {form.publicKey && !publicCheck.valid && (
                    <div className="text-danger" style={{ fontSize: 13 }}>{publicCheck.message}</div>
                )}
            </div>

            <div className="mb-3">
                <label className="form-label fw-semibold" htmlFor="cw-private-key">
                    Private key
                </label>
                <input
                    id="cw-private-key"
                    type="password"
                    className="form-control"
                    value={form.privateKey || ""}
                    disabled={busy}
                    autoComplete="new-password"
                    placeholder={hasStoredKey ? "Leave blank to keep the stored key" : ""}
                    onChange={(e) => set({ privateKey: e.target.value })}
                />
                <div className="text-muted" style={{ fontSize: 13 }}>
                    {hasStoredKey
                        ? "A key is already stored. Leave this blank to keep it, or enter a new one to replace it."
                        : "Shown by ConnectWise only once, when the key pair is created."}
                </div>
                {!privateCheck.valid && form.privateKey !== undefined && form.privateKey !== "" && (
                    <div className="text-danger" style={{ fontSize: 13 }}>{privateCheck.message}</div>
                )}
            </div>

            <div className="mb-3">
                <label className="form-label fw-semibold" htmlFor="cw-client-id">
                    Client ID
                </label>
                <input
                    id="cw-client-id"
                    type="text"
                    className="form-control"
                    value={form.clientId || ""}
                    disabled={busy}
                    onChange={(e) => set({ clientId: e.target.value })}
                />
                <div className="text-muted" style={{ fontSize: 13 }}>
                    Issued by the ConnectWise Developer Network for your integration.
                </div>
            </div>

            {testResult && (
                <div
                    className={`alert ${testResult.success ? "alert-success" : "alert-danger"} py-2`}
                    style={{ fontSize: 14 }}
                >
                    {testResult.message}
                </div>
            )}

            <div className="d-flex gap-2">
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!testable || busy}
                    onClick={onTest}
                >
                    {testing ? "Testing…" : "Test connection"}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!saveable || busy}
                    onClick={onSave}
                >
                    {saving ? "Saving…" : "Save"}
                </button>
            </div>

            {/* Greying the button out told nobody WHY. canTest already knows which field is at
                fault, so it says so rather than leaving the reseller to guess. */}
            {!testable && (
                <div className="text-muted" style={{ fontSize: 13, marginTop: 8 }}>
                    {canTest(form, hasStoredKey).message || "Fill in the connection details above."}
                </div>
            )}

            {testable && !saveable && (
                <div className="text-muted" style={{ fontSize: 13, marginTop: 8 }}>
                    Run a successful connection test to enable saving.
                </div>
            )}
        </div>
    );
}
